import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const PMTeamDashboard = () => {
  const { getMyTeamData, updateTask, users } = useData();
  const { currentUser } = useAuth();
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedMember, setSelectedMember] = useState(null);
  const [dateFilter, setDateFilter] = useState('this_month');
  const [taskFilter, setTaskFilter] = useState('all');

  // New state for attendance date navigation
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const { myProjects, teamMembers, teamAttendance, teamTasks } = getMyTeamData();

  // Only PMs and Owners can see this
  if (!['owner', 'project_manager'].includes(currentUser?.role)) {
    return (
      <div className="access-denied">
        <h3 className="access-denied-title">Access Restricted</h3>
        <p className="access-denied-text">This dashboard is only available to Project Managers and Owners.</p>
      </div>
    );
  }

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };
  const getFilteredTasks = () => {
    switch (taskFilter) {
      case 'pending':
        return teamTasks.filter(t => t.status === 'not_started');
      case 'progress':
        return teamTasks.filter(t => t.status === 'ongoing');
      case 'completed':
        return teamTasks.filter(t => t.status === 'completed');
      case 'overdue':
        return teamTasks.filter(t => {
          const today = new Date();
          const dueDate = new Date(t.dueDate);
          return dueDate < today && t.status !== 'completed';
        });
      default:
        return teamTasks;
    }
  };

  // Enhanced date navigation functions
  const navigateDate = (direction) => {
    const currentDate = new Date(selectedDate);
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1);
    } else if (direction === 'next') {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  // Get attendance for selected date
  const getSelectedDateAttendance = () => {
    return teamAttendance.filter(record => record.date === selectedDate);
  };

  // Filter attendance by date range (for analytics)
  const getFilteredAttendance = () => {
    const now = new Date();
    let startDate = new Date();

    switch (dateFilter) {
      case 'today':
        startDate = new Date(now.toDateString());
        break;
      case 'this_week':
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        return teamAttendance.filter(record => {
          const recordDate = new Date(record.date);
          return recordDate >= startDate && recordDate <= endDate;
        });
      default:
        return teamAttendance;
    }

    return teamAttendance.filter(record => {
      const recordDate = new Date(record.date);
      return recordDate >= startDate;
    });
  };
  const filteredTasks = getFilteredTasks();
  const filteredAttendance = getFilteredAttendance();
  const selectedDateAttendance = getSelectedDateAttendance();

  // Calculate team stats
  const todayDate = new Date().toISOString().split('T')[0];
  const todayAttendance = teamAttendance.filter(record => record.date === todayDate);
  const presentToday = todayAttendance.filter(record => record.checkIn).length;

  const taskStats = {
    total: teamTasks.length,
    completed: teamTasks.filter(t => t.status === 'completed').length,
    inProgress: teamTasks.filter(t => t.status === 'ongoing').length,
    pending: teamTasks.filter(t => t.status === 'not_started').length,
    overdue: teamTasks.filter(t => {
      const today = new Date();
      const dueDate = new Date(t.dueDate);
      return dueDate < today && t.status !== 'completed';
    }).length
  };

  // Get individual member stats
  const getMemberStats = (memberId) => {
    const memberAttendance = teamAttendance.filter(record => record.userId === memberId);
    const memberTasks = teamTasks.filter(task => task.assigneeId === memberId);

    const thisMonthAttendance = memberAttendance.filter(record => {
      const recordDate = new Date(record.date);
      const thisMonth = new Date();
      thisMonth.setDate(1);
      return recordDate >= thisMonth;
    });

    const totalHours = thisMonthAttendance.reduce((sum, record) => sum + (record.hoursWorked || 0), 0);

    return {
      totalTasks: memberTasks.length,
      completedTasks: memberTasks.filter(t => t.status === 'completed').length,
      daysPresent: thisMonthAttendance.length,
      totalHours: totalHours,
      avgHoursPerDay: thisMonthAttendance.length > 0 ? (totalHours / thisMonthAttendance.length).toFixed(1) : 0
    };
  };

  return (
    <div className="pm-team-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2 className="dashboard-title">
            {currentUser?.role === 'owner' ? 'Organization Analytics' : 'Team Management Dashboard'}
          </h2>
          <p className="dashboard-subtitle">
            {currentUser?.role === 'owner' 
              ? 'Monitor all projects, teams, and performance across your organization'
              : 'Manage your project teams and track progress'}
          </p>
        </div>
        <div className="header-actions">
          <select 
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="form-control date-filter-select"
          >
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Team Overview Stats */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'}}>
            <i className="fas fa-folder"></i>
          </div>
          <div className="stat-info">
            <h3 className="stat-number">{myProjects.length}</h3>
            <p className="stat-label">
              {currentUser?.role === 'owner' ? 'Total Projects' : 'My Projects'}
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-info">
            <h3 className="stat-number">{teamMembers.length}</h3>
            <p className="stat-label">
              {currentUser?.role === 'owner' ? 'All Employees' : 'Team Members'}
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
            <i className="fas fa-user-check"></i>
          </div>
          <div className="stat-info">
            <h3 className="stat-number">{presentToday}</h3>
            <p className="stat-label">Present Today</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="stat-info">
            <h3 className="stat-number">{taskStats.overdue}</h3>
            <p className="stat-label">Overdue Tasks</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`tab ${selectedView === 'overview' ? 'active' : ''}`}
          onClick={() => setSelectedView('overview')}
        >
          <i className="fas fa-chart-pie"></i>
          Overview
        </button>
        <button 
          className={`tab ${selectedView === 'tasks' ? 'active' : ''}`}
          onClick={() => setSelectedView('tasks')}
        >
          <i className="fas fa-tasks"></i>
          Team Tasks ({taskStats.total})
        </button>
        <button 
          className={`tab ${selectedView === 'attendance' ? 'active' : ''}`}
          onClick={() => setSelectedView('attendance')}
        >
          <i className="fas fa-calendar-check"></i>
          Team Attendance ({selectedDateAttendance.length})
        </button>
        <button 
          className={`tab ${selectedView === 'members' ? 'active' : ''}`}
          onClick={() => setSelectedView('members')}
        >
          <i className="fas fa-users"></i>
          Team Members ({teamMembers.length})
        </button>
        <button 
          className={`tab ${selectedView === 'analytics' ? 'active' : ''}`}
          onClick={() => setSelectedView('analytics')}
        >
          <i className="fas fa-chart-bar"></i>
          Analytics
        </button>
      </div>

      {/* Content Views */}
      <div className="dashboard-content">
        {selectedView === 'overview' && (
          <div className="overview-grid">
            {/* My Projects */}
            <div className="dashboard-card">
              <h3 className="card-title">
                {currentUser?.role === 'owner' ? 'All Projects' : 'My Projects'}
              </h3>
              {myProjects.length > 0 ? (
                <div className="project-list">
                  {myProjects.map(project => {
                    const projectTasks = teamTasks.filter(t => t.projectId === project.id);
                    const completedTasks = projectTasks.filter(t => t.status === 'completed');
                    const progress = projectTasks.length > 0 ? Math.round((completedTasks.length / projectTasks.length) * 100) : 0;

                    return (
                      <div key={project.id} className="project-item">
                        <div className="project-info">
                          <h4 className="project-name">{project.name}</h4>
                          <p className="project-namee">{project.teamMembers?.length || 0} team members</p>
                          <div className="project-progress">
                            <span className="project-name">Progress: {progress}%</span>
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <span className={`status-badge status-${project.status}`}>
                          {project.status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-data">
                  <i className="fas fa-folder-open no-data-icon"></i>
                  <h3 className="no-data-title">No Projects</h3>
                  <p className="no-data-text">
                    {currentUser?.role === 'owner' 
                      ? 'No projects in the system yet.' 
                      : 'No projects assigned to you yet.'}
                  </p>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="dashboard-card">
              <h3 className="card-title">Quick Stats</h3>
              <div className="quick-stats">
                <div className="stat-item">
                  <div className="stat-label">Task Completion Rate</div>
                  <div className="stat-value">
                    {taskStats.total > 0 ? Math.round((taskStats.completed / taskStats.total) * 100) : 0}%
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Team Attendance Today</div>
                  <div className="stat-value">
                    {teamMembers.length > 0 ? Math.round((presentToday / teamMembers.length) * 100) : 0}%
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Overdue Tasks</div>
                  <div className="stat-value stat-danger">{taskStats.overdue}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Tasks In Progress</div>
                  <div className="stat-value stat-warning">{taskStats.inProgress}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedView === 'tasks' && (
          <div className="tasks-view">
            <div className="view-header">
              <h3 className="view-title">Team Tasks Management</h3>
              <div className="task-filters">
    <div className="filter-buttons">
      <button 
        className={`btn btn-sm ${taskFilter === 'all' ? 'btn-primary' : 'btn-outline'}`}
        onClick={() => setTaskFilter('all')}
      >
        All ({taskStats.total})
      </button>
      <button 
        className={`btn btn-sm ${taskFilter === 'pending' ? 'btn-primary' : 'btn-outline'}`}
        onClick={() => setTaskFilter('pending')}
      >
        Pending ({taskStats.pending})
      </button>
      <button 
        className={`btn btn-sm ${taskFilter === 'progress' ? 'btn-primary' : 'btn-outline'}`}
        onClick={() => setTaskFilter('progress')}
      >
        Progress ({taskStats.inProgress})
      </button>
      <button 
        className={`btn btn-sm ${taskFilter === 'completed' ? 'btn-primary' : 'btn-outline'}`}
        onClick={() => setTaskFilter('completed')}
      >
        Completed ({taskStats.completed})
      </button>
      <button 
        className={`btn btn-sm ${taskFilter === 'overdue' ? 'btn-danger' : 'btn-outline'}`}
        onClick={() => setTaskFilter('overdue')}
      >
        Overdue ({taskStats.overdue})
      </button>
    </div>
  </div>
            </div>

            {filteredTasks.length > 0 ? (
    <div className="tasks-grid">
      {filteredTasks.map(task => {
        const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'completed';
        return (
          <div key={task.id} className={`task-card ${isOverdue ? 'task-overdue' : ''}`}>
            <div className="task-header">
              <h4 className="task-title">{task.title}</h4>
              <div className="task-badges">
                <span className={`status-badge status-${task.status}`}>
                  {task.status.replace('_', ' ')}
                </span>
                <span className={`priority-badge priority-${task.priority}`}>
                  {task.priority}
                </span>
              </div>
            </div>
            <p className="task-description">{task.description}</p>
            <div className="task-meta">
              <div className="task-meta-item">
                <i className="fas fa-user"></i>
                <span className="meta-text">{task.assigneeName}</span>
              </div>
              <div className="task-meta-item">
                <i className="fas fa-calendar"></i>
                <span className="meta-text">
                  Due: {new Date(task.dueDate).toLocaleDateString()}
                  {isOverdue && <span className="overdue-label">OVERDUE</span>}
                </span>
              </div>
              <div className="task-meta-item">
                <i className="fas fa-project-diagram"></i>
                <span className="meta-text">{task.projectName}</span>
              </div>
            </div>

            <div className="task-actions">
              {task.status === 'ongoing' && (
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => handleTaskStatusChange(task.id, 'completed')}
                >
                  <i className="fas fa-check"></i>
                  Mark Complete
                </button>
              )}
              {task.status === 'not_started' && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleTaskStatusChange(task.id, 'ongoing')}
                >
                  <i className="fas fa-play"></i>
                  Start Task
                </button>
              )}
              {task.status === 'completed' && (
                <span className="task-completed">
                  <i className="fas fa-check-circle"></i>
                  Completed
                </span>
              )}
            </div>
          </div>
        );
      })}
              </div>
            ) : (
              <div className="no-data">
                <i className="fas fa-tasks no-data-icon"></i>
                <h3 className="no-data-title">No Tasks</h3>
                <p className="no-data-text">No tasks assigned to your team projects yet.</p>
              </div>
            )}
          </div>
        )}

        {selectedView === 'attendance' && (
          <div className="attendance-view">
            <div className="attendance-header">
              <h3 className="view-title">Team Attendance</h3>

              {/* Enhanced Date Navigation */}
              <div className="date-navigation">
                <button 
                  className="btn btn-sm btn-outline date-nav-btn"
                  onClick={() => navigateDate('prev')}
                  title="Previous Day"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>

                <div className="date-display">
                  <button 
                    className="current-date-btn"
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    title="Click to select date"
                  >
                    <i className="fas fa-calendar"></i>
                    {new Date(selectedDate).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                    <i className="fas fa-chevron-down"></i>
                  </button>

                  {showDatePicker && (
                    <div className="date-picker-dropdown">
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => {
                          setSelectedDate(e.target.value);
                          setShowDatePicker(false);
                        }}
                        className="date-picker-input"
                      />
                    </div>
                  )}
                </div>

                <button 
                  className="btn btn-sm btn-outline date-nav-btn"
                  onClick={() => navigateDate('next')}
                  title="Next Day"
                  disabled={selectedDate === new Date().toISOString().split('T')[0]}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>

                <button 
                  className="btn btn-sm btn-primary"
                  onClick={goToToday}
                  title="Go to Today"
                >
                  <i className="fas fa-home"></i>
                  Today
                </button>
              </div>
            </div>

            <div className="attendance-summary">
              <div className="summary-item">
                <span className="summary-label">Present:</span>
                <span className="summary-value summary-success">
                  {selectedDateAttendance.filter(record => record.checkIn).length}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Team:</span>
                <span className="summary-value">{teamMembers.length}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Attendance Rate:</span>
                <span className="summary-value">
                  {teamMembers.length > 0 
                    ? Math.round((selectedDateAttendance.filter(record => record.checkIn).length / teamMembers.length) * 100)
                    : 0}%
                </span>
              </div>
            </div>

            {selectedDateAttendance.length > 0 ? (
              <div className="attendance-grid">
                {teamMembers.map(member => {
    const memberRecord = selectedDateAttendance.find(record => record.userId === member.id);
    const isPresent = memberRecord && memberRecord.checkIn;
  
    return (
      <div key={member.id} className={`attendance-card ${isPresent ? 'present' : 'absent'}`}>
        <div className="member-info">
          <div className="member-avatar">
            <i className="fas fa-user"></i>
          </div>
          <div className="member-details">
            <h4 className="member-name">{member.name}</h4>
            <p className="member-role">{member.role.replace('_', ' ')}</p>
          </div>
        </div>
  
        <div className="attendance-info">
          {isPresent ? (
            <>
              <div className="attendance-status status-present">
                <i className="fas fa-check-circle"></i>
                <span>Present</span>
              </div>
              <div className="time-info">
                <div className="time-item">
                  <span className="time-label">Check In:</span>
                  <span className="time-value">{memberRecord.checkIn}</span>
                </div>
                <div className="time-item">
                  <span className="time-label">Check Out:</span>
                  <span className="time-value">{memberRecord.checkOut || 'Working'}</span>
                </div>
                <div className="time-item">
                  <span className="time-label">Hours:</span>
                  <span className="time-value hours-worked">
                    {memberRecord.hoursWorked || 0}h
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="attendance-status status-absent">
                <i className="fas fa-times-circle"></i>
                <span>Absent</span>
              </div>
              <div className="time-info">
                <div className="time-item">
                  <span className="time-label">Status:</span>
                  <span className="time-value">No attendance record</span>
                </div>
                <div className="time-item">
                  <span className="time-label">Hours:</span>
                  <span className="time-value">0h</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  })}
              </div>
            ) : (
              <div className="no-data">
                <i className="fas fa-calendar-times no-data-icon"></i>
                <h3 className="no-data-title">No Attendance Records</h3>
                <p className="no-data-text">No attendance data available for {new Date(selectedDate).toLocaleDateString()}.</p>
              </div>
            )}
          </div>
        )}

        {selectedView === 'members' && (
          <div className="members-view">
            <h3 className="view-title">Team Members Overview</h3>
            {teamMembers.length > 0 ? (
              <div className="members-grid">
                {teamMembers.map(member => {
                  const memberStats = getMemberStats(member.id);
                  const memberAttendanceToday = todayAttendance.find(
                    record => record.userId === member.id
                  );

                  return (
                    <div key={member.id} className="member-card">
                      <div className="member-header">
                        <div className="member-avatar">
                          <i className="fas fa-user"></i>
                        </div>
                        <div className="member-info">
                          <h4 className="member-name">{member.name}</h4>
                          <p className="member-role">{member.role.replace('_', ' ')}</p>
                          <p className="member-department">{member.department}</p>
                        </div>
                        <div className="member-status">
                          {memberAttendanceToday ? (
                            <div className="attendance-status status-present">
                              <i className="fas fa-check-circle"></i>
                              <span>Present</span>
                            </div>
                          ) : (
                            <div className="attendance-status status-absent">
                              <i className="fas fa-times-circle"></i>
                              <span>Absent</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="member-stats">
                        <div className="stat-row">
                          <div className="stat-item">
                            <span className="stat-label">Tasks Completed</span>
                            <span className="stat-value stat-success">
                              {memberStats.completedTasks}/{memberStats.totalTasks}
                            </span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Days Present</span>
                            <span className="stat-value">{memberStats.daysPresent}</span>
                          </div>
                        </div>
                        <div className="stat-row">
                          <div className="stat-item">
                            <span className="stat-label">Total Hours</span>
                            <span className="stat-value">
                              {memberStats.totalHours.toFixed(2)}h
                            </span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Avg Hours/Day</span>
                            <span className="stat-value">{memberStats.avgHoursPerDay}h</span>
                          </div>
                        </div>
                      </div>

                      {memberAttendanceToday && (
                        <div className="today-attendance">
                          <small className="attendance-detail">
                            Today: {memberAttendanceToday.checkIn} - {memberAttendanceToday.checkOut || 'Working'} 
                            ({memberAttendanceToday.hoursWorked || 0}h)
                          </small>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-data">
                <i className="fas fa-user-friends no-data-icon"></i>
                <h3 className="no-data-title">No Team Members</h3>
                <p className="no-data-text">
                  {currentUser?.role === 'owner' 
                    ? 'No employees in the system yet.' 
                    : 'No team members assigned to your projects yet.'}
                </p>
              </div>
            )}
          </div>
        )}

        {selectedView === 'analytics' && (
          <div className="analytics-view">
            <h3 className="view-title">Team Performance Analytics</h3>

            <div className="analytics-grid">
              <div className="analytics-card">
                <h4 className="card-titlee">Task Completion Trends</h4>
                <div className="completion-stats">
                  <div className="completion-item">
                    <span className="completion-label">This Week</span>
                    <div className="completion-bar">
                      <div 
                        className="completion-fill" 
                        style={{width: `${taskStats.total > 0 ? (taskStats.completed / taskStats.total) * 100 : 0}%`}}
                      ></div>
                    </div>
                    <span className="completion-percent">
                      {taskStats.total > 0 ? Math.round((taskStats.completed / taskStats.total) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="analytics-card">
                <h4 className="card-titlee">Attendance Patterns</h4>
                <div className="attendance-analytics">
                  <div className="attendance-stat">
                    <span className="stat-label">Average Daily Attendance</span>
                    <span className="stat-value">
                      {teamMembers.length > 0 ? Math.round((presentToday / teamMembers.length) * 100) : 0}%
                    </span>
                  </div>
                  <div className="attendance-stat">
                    <span className="stat-label">Total Hours This Month</span>
                    <span className="stat-value">
                      {filteredAttendance.reduce((sum, record) => sum + (record.hoursWorked || 0), 0)}h
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PMTeamDashboard;